import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Toast + Confirm: lightweight, dependency-free notification system.
 *
 * Setup (once, at the app root):
 *   <ToastProvider><App /></ToastProvider>
 *
 * Usage:
 *   const { toast, confirm } = useToast();
 *
 *   toast.success("Checked in");
 *   toast.error("Could not save changes", { duration: 6000 });
 *   toast.info("Sync in progress");
 *
 *   // Simple confirm (message, title)
 *   const ok = await confirm("You won't be able to undo this.", "Check out now?");
 *
 *   // Confirm with options
 *   const ok = await confirm("This leave request will be deleted.", {
 *     title: "Delete request?",
 *     confirmLabel: "Delete",
 *     danger: true,
 *   });
 *
 * All times shown across the module are in IST.
 */

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const DEFAULT_DURATION_MS = 3800;
const ERROR_DURATION_MS = 5500;
const MAX_VISIBLE_TOASTS = 4;

const DEFAULT_CONFIRM = Object.freeze({
  title: "Are you sure?",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  danger: false,
});

const FOCUSABLE = "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

const ToastContext = createContext(null);

/** Fallback used when a component renders outside <ToastProvider>. */
const FALLBACK_API = {
  toast: {
    success: (m) => window.alert(m),
    error: (m) => window.alert(m),
    info: (m) => window.alert(m),
    dismiss: () => {},
  },
  confirm: async (message) => window.confirm(message),
};

export function useToast() {
  return useContext(ToastContext) ?? FALLBACK_API;
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const iconProps = {
  viewBox: "0 0 24 24",
  width: 14,
  height: 14,
  fill: "none",
  stroke: "#fff",
  strokeWidth: 2.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: "false",
};

const ICONS = {
  success: (
    <svg {...iconProps}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg {...iconProps}>
      <path d="M12 8v5M12 17h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  info: (
    <svg {...iconProps}>
      <path d="M12 11v5M12 8h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
};

const CloseIcon = () => (
  <svg {...iconProps} stroke="currentColor" strokeWidth={2}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Confirm dialog                                                      */
/* ------------------------------------------------------------------ */

function ConfirmDialog({ dialog, onClose }) {
  const boxRef = useRef(null);
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    confirmBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose(false);
        return;
      }
      // Keep keyboard focus inside the dialog while it is open
      if (e.key === "Tab" && boxRef.current) {
        const nodes = boxRef.current.querySelectorAll(FOCUSABLE);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="atl-confirm" onClick={() => onClose(false)}>
      <div
        ref={boxRef}
        className="atl-confirm__box"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={dialog.titleId}
        aria-describedby={dialog.messageId}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 id={dialog.titleId}>{dialog.title}</h4>
        <p id={dialog.messageId}>{dialog.message}</p>
        <div className="atl-confirm__actions">
          <button
            type="button"
            className="atl-btn atl-btn--secondary atl-btn--sm"
            onClick={() => onClose(false)}
          >
            {dialog.cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`atl-btn atl-btn--sm ${dialog.danger ? "atl-btn--danger" : "atl-btn--primary"}`}
            onClick={() => onClose(true)}
          >
            {dialog.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);

  const idRef = useRef(0);
  const timersRef = useRef(new Map());
  const resolverRef = useRef(null);

  /* ---- Toasts ---- */

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type, message, options = {}) => {
      const id = ++idRef.current;
      const duration =
        options.duration ?? (type === "error" ? ERROR_DURATION_MS : DEFAULT_DURATION_MS);

      setToasts((list) => {
        const next = [...list, { id, type, message }];
        // Drop the oldest toasts (and their timers) once the cap is exceeded
        const overflow = next.length - MAX_VISIBLE_TOASTS;
        if (overflow > 0) {
          next.slice(0, overflow).forEach((t) => {
            clearTimeout(timersRef.current.get(t.id));
            timersRef.current.delete(t.id);
          });
          return next.slice(overflow);
        }
        return next;
      });

      if (duration > 0) {
        timersRef.current.set(id, setTimeout(() => dismiss(id), duration));
      }
      return id;
    },
    [dismiss]
  );

  const toast = useMemo(
    () => ({
      success: (m, o) => push("success", m, o),
      error: (m, o) => push("error", m, o),
      info: (m, o) => push("info", m, o),
      dismiss,
    }),
    [push, dismiss]
  );

  /* ---- Confirm ---- */

  const closeDialog = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    resolve?.(value);
  }, []);

  const confirm = useCallback((message, titleOrOptions) => {
    return new Promise((resolve) => {
      // Only one dialog at a time: settle any pending one as "cancelled"
      resolverRef.current?.(false);
      resolverRef.current = resolve;

      const options =
        typeof titleOrOptions === "string"
          ? { title: titleOrOptions }
          : titleOrOptions ?? {};
      const uid = ++idRef.current;

      setDialog({
        ...DEFAULT_CONFIRM,
        ...options,
        message,
        titleId: `atl-confirm-title-${uid}`,
        messageId: `atl-confirm-msg-${uid}`,
      });
    });
  }, []);

  /* ---- Cleanup on unmount ---- */

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
      resolverRef.current?.(false);
      resolverRef.current = null;
    };
  }, []);

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div className="atl-toast-wrap" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`atl-toast atl-toast--${t.type}`}
            role={t.type === "error" ? "alert" : "status"}
          >
            <span className="atl-toast__ic">{ICONS[t.type]}</span>
            <span className="atl-toast__msg">{t.message}</span>
            <button
              type="button"
              className="atl-toast__close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>

      {dialog && <ConfirmDialog dialog={dialog} onClose={closeDialog} />}
    </ToastContext.Provider>
  );
}